import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Rocket, Check, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const BoostDialog = ({ product, open, onOpenChange, onBoosted }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (open) api.get("/boost/status").then(({ data }) => setStatus(data)).catch(() => setStatus({}));
  }, [open]);

  const active = status?.active?.find((a) => a.product_id === product.id);
  const freeLeft = status?.free_max_remaining || 0;

  const start = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/boost/start", { product_id: product.id, tier, origin_url: window.location.origin });
      if (data.activated) {
        toast.success(`Boost active for 24h — ${data.free_max_remaining} free boost${data.free_max_remaining === 1 ? "" : "s"} left this month`);
        onBoosted?.();
        onOpenChange(false);
      } else {
        window.location.assign(data.checkout_url);
      }
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Could not start boost");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0E0E10] border-[#27272A]" data-testid="boost-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Rocket className="h-4 w-4 text-[#FF3B30]" /> Boost “{product.title}”</DialogTitle></DialogHeader>
        <p className="text-sm text-neutral-400">Push this listing into more buyer feeds for 24 hours. Boosts stack onto any time already running.</p>
        {active && (
          <div className="rounded-lg border border-[#34C759]/40 bg-[#34C759]/10 p-3 text-sm text-[#34C759] flex items-center gap-2" data-testid="boost-active-note">
            <Clock className="h-4 w-4" /> {active.tier === "max" ? "Max" : "Lite"} boost running until {new Date(active.ends_at).toLocaleString()}
          </div>
        )}
        {status?.free_max_per_month > 0 && (
          <div className="text-xs text-neutral-400" data-testid="boost-free-note">
            {freeLeft > 0 ? <><span className="text-white font-semibold">{freeLeft}</span> free Max boost{freeLeft === 1 ? "" : "s"} left this month (included in your subscription).</>
              : "Subscribed sellers get 3 free Max boosts every month."}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {status?.tiers && Object.entries(status.tiers).map(([key, t]) => {
            const free = key === "max" && freeLeft > 0;
            return (
              <div key={key} className={`rounded-xl border p-4 ${key === "max" ? "border-[#FF3B30]/50 bg-[#FF3B30]/5" : "border-[#27272A] bg-[#121212]"}`} data-testid={`boost-tier-${key}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-bold">{t.label}</div>
                  <div className="font-mono font-bold text-[#34C759]">{free ? "FREE" : `$${t.price_usd.toFixed(2)}`}</div>
                </div>
                <p className="mt-1 text-xs text-neutral-400 min-h-[32px]">{t.blurb}</p>
                <Button onClick={() => start(key)} disabled={!!busy} data-testid={`boost-start-${key}`}
                  className={`mt-3 w-full rounded-full font-semibold ${key === "max" ? "bg-[#FF3B30] hover:bg-[#e0352b] text-white" : "bg-white text-black hover:bg-neutral-200"}`}>
                  {busy === key ? "Starting…" : free ? <><Check className="h-4 w-4 mr-1" /> Use free boost</> : `Boost for $${t.price_usd.toFixed(0)}`}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-neutral-500">Paid boosts are charged by card via secure checkout and start the moment payment completes.</p>
      </DialogContent>
    </Dialog>
  );
};
